import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const AttendanceDuplicates = ({ open, onClose, onResolved }) => {
  const [groups, setGroups] = useState([]);
  const [keep, setKeep] = useState({});
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/attendance/lock-status").then((r) => setLocked(r.data.locked)).catch(() => {});
    api.get("/attendance/duplicates").then((r) => setGroups(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) { setKeep({}); load(); } }, [open]);

  const resolve = async (g, gi) => {
    const id = keep[gi];
    if (!id) return toast.error("Pilih data yang dipertahankan");
    try {
      const r = await api.post("/attendance/duplicates/resolve", { keep_id: id });
      toast.success(`${g.employee_nama}: ${r.data.removed} data dobel dihapus${r.data.locked ? " — pengaman dobel permanen kini AKTIF" : ""}`);
      setKeep({}); load(); onResolved?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const pickActive = () => {
    const k = {};
    groups.forEach((g, gi) => { const m = g.records.find((r) => r.project_id && r.project_id === g.proyek_aktif_id) || g.records[g.records.length - 1]; k[gi] = m.id; });
    setKeep(k);
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  const resolveAllMarked = async () => {
    const ids = Object.values(keep);
    if (!ids.length) return toast.error("Belum ada pilihan yang ditandai");
    if (!window.confirm(`Pertahankan ${ids.length} data yang ditandai dan hapus dobel lainnya?`)) return;
    setBulkBusy(true);
    let removed = 0; let locked = false;
    for (const id of ids) {
      try { const r = await api.post("/attendance/duplicates/resolve", { keep_id: id }); removed += r.data.removed; locked = locked || r.data.locked; }
      catch (e) { toast.error(apiErr(e)); }
    }
    toast.success(`${removed} data dobel dihapus${locked ? " — pengaman dobel permanen kini AKTIF" : ""}`);
    setBulkBusy(false); setKeep({}); load(); onResolved?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="att-dup-dialog">
        <DialogHeader><DialogTitle>Absensi Dobel ({groups.length})</DialogTitle></DialogHeader>
        {locked !== null && (
          <div className={`rounded-lg px-3 py-2 text-sm ${locked ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="att-lock-status">
            {locked ? <><b>Pengaman dobel permanen AKTIF</b> — database menolak absensi ke-2 untuk karyawan & tanggal yang sama.</>
              : <><b>Pengaman dobel permanen belum aktif.</b> Rapikan semua data dobel di bawah; pengaman otomatis aktif setelah daftar kosong.</>}
          </div>
        )}
        {groups.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Button size="sm" variant="outline" onClick={pickActive} data-testid="att-dup-pick-active">Tandai otomatis: sesuai proyek aktif</Button>
            <Button size="sm" className="bg-rose-700 hover:bg-rose-800" disabled={bulkBusy || !Object.keys(keep).length} onClick={resolveAllMarked} data-testid="att-dup-resolve-all">{bulkBusy ? "Memproses…" : `Pertahankan Semua yang Ditandai (${Object.keys(keep).length})`}</Button>
            <span>Periksa tanda pilihan dulu sebelum memproses.</span>
          </div>
        )}
        {loading ? <p className="text-sm text-slate-500">Memuat...</p>
          : groups.length === 0 ? <p className="py-6 text-center text-sm text-emerald-700" data-testid="att-dup-empty">Tidak ada absensi dobel.</p>
          : groups.map((g, gi) => (
            <div key={`${g.tanggal}-${g.employee_id}`} className="rounded-xl border border-slate-200 p-3" data-testid={`att-dup-group-${gi}`}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <div><b className="text-slate-800">{g.employee_nama}</b> <span className="text-xs text-slate-400">{g.kode_pegawai}</span>
                  <div className="text-xs text-slate-500">{formatDate(g.tanggal)} · Proyek aktif: <b>{g.proyek_aktif}</b> · Mandor: <b>{g.mandor}</b></div></div>
                <Button size="sm" className="bg-blue-800 hover:bg-blue-900" disabled={!keep[gi]} data-testid={`att-dup-keep-btn-${gi}`} onClick={() => resolve(g, gi)}>Pertahankan yang dipilih</Button>
              </div>
              {g.records.map((r) => (
                <label key={r.id} className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm mb-1 ${keep[gi] === r.id ? "border-blue-400 bg-blue-50" : "border-slate-100"}`}>
                  <input type="radio" name={`dup-${gi}`} className="accent-blue-800" checked={keep[gi] === r.id} onChange={() => setKeep({ ...keep, [gi]: r.id })} data-testid={`att-dup-radio-${r.id}`} />
                  <span className="flex-1"><b>{r.project_nama}</b> · {r.status} · {r.jam_masuk || "-"}–{r.jam_pulang || "-"} · lembur {r.jam_lembur || 0} j</span>
                  <span className="text-xs text-slate-400">input: {r.input_by}</span>
                </label>
              ))}
            </div>
          ))}
      </DialogContent>
    </Dialog>
  );
};

export const AttendanceMoves = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) api.get("/attendance/moves").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="att-moves-dialog">
        <DialogHeader><DialogTitle>Riwayat Pindah Proyek Absensi ({rows.length})</DialogTitle></DialogHeader>
        <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              <th className="px-3 py-2 text-left">Tgl Absen</th><th className="px-3 py-2 text-left">Pegawai</th>
              <th className="px-3 py-2 text-left">Proyek Lama</th><th className="px-3 py-2 text-left">Proyek Baru</th>
              <th className="px-3 py-2 text-left">Input Lama</th><th className="px-3 py-2 text-left">Dipindah Oleh</th><th className="px-3 py-2 text-left">Waktu</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="py-6 text-center text-slate-400" data-testid="att-moves-empty">Belum ada absensi yang dipindah.</td></tr>
                : rows.map((m, i) => (
                  <tr key={m.id} className="border-b border-slate-100" data-testid={`att-move-${i}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(m.tanggal)}</td><td className="px-3 py-2 font-semibold">{m.employee_nama}</td>
                    <td className="px-3 py-2 text-rose-700">{m.from_project}</td><td className="px-3 py-2 text-emerald-700">{m.to_project}</td>
                    <td className="px-3 py-2">{m.input_lama}</td><td className="px-3 py-2">{m.moved_by}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">{formatDateTime(m.moved_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
